import { Sprite, Texture, Graphics } from 'pixi.js';

// Textura compartida - se crea una sola vez para todos los sprites
let sharedRectTexture = null;

/**
 * Obtiene o crea la textura compartida de 1x1 pixel blanco
 * Todos los PicSprite usan esta misma textura, permitiendo batching máximo
 * Se regenera si la textura anterior fue destruida (navegación SPA)
 */
function getSharedRectTexture(renderer) {
  if (!sharedRectTexture || sharedRectTexture.destroyed) {
    const graphics = new Graphics();
    graphics.rect(0, 0, 1, 1).fill(0xFFFFFF);
    sharedRectTexture = renderer.generateTexture(graphics);
    graphics.destroy();
  }
  return sharedRectTexture;
}

/**
 * Convierte color hex string a número
 */
function hexToNumber(hex) {
  if (!hex) return 0xFEFEFE;
  if (typeof hex === 'number') return hex;
  return parseInt(hex.replace('#', ''), 16);
}

// Línea por defecto de elementos SIN estado (gris plomo). En cuanto el elemento
// recibe un estado, la línea pasa al color de su capa CAD.
export const NO_STATUS_LINE_COLOR = '#e0e0e0';
export const NO_STATUS_LINE_ALPHA = 0.8;

/**
 * PicSprite - Versión optimizada de Pic usando Sprite con textura compartida
 *
 * Ventajas sobre Graphics:
 * - Tinting es GRATIS en WebGL (cambio de color sin redibujado)
 * - Todos los sprites comparten la misma textura = batching máximo
 * - Un draw call para miles de entidades en lugar de uno por cada una
 */
export class PicSprite extends Sprite {

  constructor(el, offset, layers, layerCAD, index, typeRegister, renderer) {
    super(getSharedRectTexture(renderer));

    // Identificación
    this.picId = el.id.toString();
    this.picName = this._GetNameEntity(el, index);
    this.picType = el.type;
    this.picLayerName = el.layer;

    // Posición (guardamos las coordenadas originales para bounds)
    this.picX = parseFloat(Math.abs(el.x + offset.x));
    this.picY = parseFloat(Math.abs(el.y - offset.y));
    this.picWidth = el.width;
    this.picHeight = el.height;

    // Aplicar posición y tamaño al sprite
    this.x = this.picX;
    this.y = this.picY;
    this.width = this.picWidth;
    this.height = this.picHeight;

    // Pre-calcular bounds para culling (evita recálculos)
    this._minX = this.picX;
    this._maxX = this.picX + this.picWidth;
    this._minY = this.picY;
    this._maxY = this.picY + this.picHeight;
    this._boundsCached = true;

    // Estado y colores
    this.picWithIssue = el.withIssue || false;
    this.picStatus = el.status ? (typeof el.status === 'object' ? el.status.id : el.status) : null;
    this.picPreviouStatus = el.so;
    this.picStatusColor = this._GetColorStatusOfLayout(el, layers);

    // Color base según tipo de registro
    this.picFillColor = typeRegister === 'issue'
      ? (this.picWithIssue ? '#fc0000' : '#f5f5f5')
      : this.picStatusColor;

    // Incidentes
    this.picIncident = el.incident || null;
    this.picIncidentColor = this._GetColorIncidentStatusOfLayout(el, layers);
    this.picPreviouIncident = null;
    this.picShowOperationalStatuses = false;

    // Línea/borde (renderizado por PixiViewer en bordersContainer, no como hijo)
    this.picLineColor = typeRegister === 'issue' ? '#706f6f' : this._GetColorLayer(el.layer, layerCAD);
    this.picLineWidth = el.type === 'sections' || el.type === 'nodes' ? 0.5 : 0.15;
    // Línea gris mientras no tenga estado (no aplica a líneas de evacuación ni a registro de incidencias)
    this.picGrayLineWithoutStatus = typeRegister !== 'issue' && el.type !== 'sections' && el.type !== 'nodes';

    // Referencia al Graphics de borde dinámico (asignada por PixiViewer si tiene incidente)
    this._border = null;
    // Contenedor de bordes (asignado por PixiViewer) para crear un borde propio al cambiar de estado
    this._bordersContainer = null;

    // Selección y hover
    this.picColorHover = '#fff7b3';
    this.picOpacityHover = 0.3;
    this.picColorSelected = '#90caf9';
    this.picOpacitySelected = 0.6;
    this.picSelected = false;

    // Aplicar color inicial mediante tint
    this._applyTint();

    // Alpha: igual que Pic.js - 0.1 solo para negro, 0.7 para el resto
    this.alpha = this.picFillColor === '#000000' ? 0.1 : 0.7;

    // Desactivar eventos individuales - usamos event delegation
    this.eventMode = 'passive';
  }

  /**
   * Redibuja el borde dinámico (solo si tiene _border asignado por PixiViewer).
   * Si tiene incidente, usa línea gruesa con color del incidente.
   * Si no tiene incidente, restaura al borde normal de la capa.
   */
  _drawBorder() {
    if (!this._border) return;

    this._border.clear();

    const { width, color, alpha, pixelLine } = this.getLineStyle();

    this._border.rect(this.picX, this.picY, this.picWidth, this.picHeight)
      .stroke({ width, color, alpha, pixelLine, join: 'miter', cap: 'butt', alignment: 0.5 });
  }

  /**
   * Estilo de la línea del elemento:
   * - con incidencia (fuera del modo incidencias): línea gruesa con color de la incidencia
   * - sin estado: gris plomo semitransparente
   * - con estado: color de la capa CAD
   *
   * @param {boolean} usePixelLine - zoom lejano: línea fija de 1px en pantalla (como AutoCAD).
   *   Por defecto toma el modo actual del contenedor de bordes (lo decide PixiViewer según el zoom).
   *   Solo aplica a los elementos con línea gris (mecánico); líneas de evacuación no cambian.
   */
  getLineStyle(usePixelLine = this._bordersContainer?._pixelLine === true) {
    const pixelLine = usePixelLine && this.picGrayLineWithoutStatus;

    if (this.picIncident && !this.picShowOperationalStatuses) {
      return { width: 0.5, color: hexToNumber(this.picIncidentColor) || 0xff0000, alpha: 1, pixelLine };
    }

    if (this.picGrayLineWithoutStatus && this.picStatus == null) {
      return { width: this.picLineWidth, color: hexToNumber(NO_STATUS_LINE_COLOR), alpha: NO_STATUS_LINE_ALPHA, pixelLine };
    }

    return { width: this.picLineWidth, color: hexToNumber(this.picLineColor) || 0x000000, alpha: 1, pixelLine };
  }

  /**
   * Garantiza que el elemento tenga un borde propio (dinámico). Los bordes estáticos
   * se dibujan en un único Graphics combinado al renderizar la capa, así que cuando el
   * estado cambia en caliente creamos un borde individual encima para reflejar el nuevo color.
   */
  _ensureOwnBorder() {
    if (this._border || !this._bordersContainer) return;

    const border = new Graphics();
    border.eventMode = 'none';
    border._pic = this;
    border._minX = this._minX;
    border._maxX = this._maxX;
    border._minY = this._minY;
    border._maxY = this._maxY;
    border._boundsCached = true;
    border.renderable = this.renderable;

    this._border = border;
    this._bordersContainer.addChild(border);
  }

  /**
   * Aplica el color usando tint (operación gratuita en WebGL)
   */
  _applyTint() {
    if (this.picSelected) {
      this.tint = hexToNumber(this.picColorSelected);
    } else if (this.picShowOperationalStatuses && this.picIncident) {
      this.tint = hexToNumber(this.picIncidentColor);
    } else if (this.picFillColor) {
      this.tint = hexToNumber(this.picFillColor);
    } else {
      this.tint = 0xFEFEFE;
    }
  }

  /**
   * Aplica efecto hover (llamado desde event delegation en PixiViewer)
   */
  applyHover() {
    this.tint = hexToNumber(this.picColorHover);
    this.alpha = 0.6;
  }

  /**
   * Remueve efecto hover (llamado desde event delegation en PixiViewer)
   */
  removeHover() {
    if (this.picSelected) {
      this.picFillColor = this.picColorSelected;
      this.alpha = this.picOpacitySelected;
    } else {
      this.picFillColor = this.picStatusColor || '#fefefe';
      this.alpha = this.picStatus ? 0.7 : 1;
    }
    this._applyTint();
  }

  /**
   * Selecciona/deselecciona el pic (llamado desde event delegation)
   */
  SelectPic(el) {
    if (!this.picSelected) {
      this.picSelected = true;
      this.picFillColor = this.picColorSelected;
      this.alpha = 0.6;
    } else {
      this.Deselect(el);
    }
    this._applyTint();
    this._Emit('select', this);
  }

  Deselect(el) {
    this.picSelected = false;
    this.picFillColor = this.picStatusColor;
    this.alpha = this.picStatus ? 0.7 : 1;
    this._applyTint();
    this._Emit('deselect', this);
  }

  /**
   * Métodos de estado - mantienen compatibilidad con Pic original
   */
  setPicStatus(status) {
    this.picStatus = status;
  }

  setPicStatusColor(color) {
    this.picStatusColor = color;
    this._applyTint();
  }

  setPicPreviouStatus(status) {
    this.picPreviouStatus = status;
  }

  setWithIssue(flag) {
    this.picWithIssue = flag;
    this.picFillColor = flag ? '#fc0000' : '#f5f5f5';
    this._applyTint();
  }

  setCurrentStatus(newStatus) {
    if (newStatus == null) return;

    if (newStatus.type === 'incident') {
      this.picIncident = newStatus.id;
      this.picIncidentColor = newStatus.color;
      if (newStatus.color) {
        this.picFillColor = newStatus.color;
      }
    } else {
      this.picStatus = newStatus.id;
      this.picStatusColor = newStatus.color;
      this.picFillColor = newStatus.color;
    }

    this.alpha = 0.7;
    this._applyTint();
    this._ensureOwnBorder();
    this._drawBorder();
  }

  applyStatus(status) {
    const statusId = status?.id ?? null;
    const statusColor = status?.color ?? null;

    if (this.picStatus === statusId) {
      if (this.picSelected) {
        this.Deselect(this);
      }
      return false;
    }

    this.picPreviouStatus = this.picStatus;
    this.picStatus = statusId;
    this.picStatusColor = statusColor;
    this.picFillColor = statusColor;
    this.picSelected = false;
    this.alpha = 0.7;
    this._applyTint();
    // La línea pasa de gris (sin estado) al color de la capa, o al revés
    this._ensureOwnBorder();
    this._drawBorder();

    return true;
  }

  showOperationalStatus(show) {
    this.picShowOperationalStatuses = show;
    this._applyTint();
    this._drawBorder();
  }

  /**
   * Métodos auxiliares
   */
  _GetNameEntity(entity, index) {
    if (entity.name != null) return entity.name;
    return 'E.' + entity.type.charAt(0).toUpperCase() + '.' + (index + 1);
  }

  _GetColorStatusOfLayout(entity, layers) {
    if (entity.status != null) {
      const lyr = layers.find(item => item.code === entity.type);
      if (lyr) {
        const idStatus = typeof entity.status === 'object' ? entity.status.id : entity.status;
        const statuses = [...(lyr.statuses ?? [])];
        const status = statuses.find(item => item.id == idStatus);
        if (status) return status.color;
      }
    }
    return null;
  }

  _GetColorIncidentStatusOfLayout(entity, layers) {
    if (entity?.incident !== undefined && entity.incident !== null && entity.incident !== '') {
      const lyr = layers.find(item => item.code === entity.type);
      if (lyr) {
        const idStatus = typeof entity.incident === 'object' ? entity.incident.id : entity.incident;
        const statuses = [...(lyr.incident_statuses ?? [])];
        const status = statuses.find(item => item.id == idStatus);
        if (status) return status.color;
      }
    }
    return null;
  }

  _GetColorLayer(nameLayer, layers) {
    if (layers === undefined) return '#000000';
    const layer = layers.find(l => l.name === nameLayer);
    if (!layer) return '#000000';
    return this._GetCssColor(layer.color);
  }

  _GetCssColor(value) {
    let s = value.toString(16);
    while (s.length < 6) {
      s = '0' + s;
    }
    return '#' + s;
  }

  _Emit(eventName, data = null) {
    document.dispatchEvent(new CustomEvent('__epc_pic_' + eventName, { detail: data }));
  }
}
